import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";

// Schema compartido por ActivarCuenta (primera contraseña) y ResetPassword
// (recuperación): mismas reglas, mismo formulario — no lo dupliques.
export const passwordSchema = z
  .object({
    password: z.string().min(8, "Mínimo 8 caracteres."),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Las contraseñas no coinciden.",
    path: ["password_confirmation"],
  });

export type DefinirPasswordValues = z.infer<typeof passwordSchema>;

interface DefinirPasswordFormProps {
  form: UseFormReturn<DefinirPasswordValues>;
  onSubmit: (values: DefinirPasswordValues) => void;
  isSubmitting: boolean;
  submitLabel?: string;
}

/**
 * Formulario de "nueva contraseña" (contraseña + confirmación), reutilizado
 * por ActivarCuenta y ResetPassword. El `form` (useForm + zodResolver) lo
 * arma cada página para poder mapear errores 422 del API con `setError`.
 */
export function DefinirPasswordForm({
  form,
  onSubmit,
  isSubmitting,
  submitLabel = "Guardar contraseña",
}: DefinirPasswordFormProps) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contraseña</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="••••••••"
                  {...field}
                />
              </FormControl>
              <FormDescription>Mínimo 8 caracteres.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password_confirmation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirmar contraseña</FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder="••••••••"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" size="lg" className="w-full gap-2" disabled={isSubmitting}>
          {isSubmitting ? <Spinner className="text-primary-foreground" /> : submitLabel}
        </Button>
      </form>
    </Form>
  );
}
